import React, { ChangeEvent, useEffect, useState } from "react";
import { useFormik } from "formik";
import type { ChecklistDetailsProps } from "./types";
import { Button, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { RootStates } from "redux/store";
import FormInput from "components/FormInput";
import toast from "react-hot-toast";
import { FormCaptureInput } from "components/common/FormCaptureInput";
import { setChecklistDetails } from "redux/conductInspectionSlice";
import { useNavigate, useParams } from "react-router-dom";
import Modal from "components/common/Modal/Modal";
import { useTranslation } from "react-i18next";
import "./ChecklistDetails.scss";

export type QuestionData = {
    response: string;
    requireReinspection: boolean;
    comments: {
        comment: string;
        documents: { docType: "image"; url: string }[];
        inspectionComment?: boolean;
    }[];
    documents: { docType: "image"; url: string }[];
    markFilled: boolean;
    question?: string;
};

type InitialValues = {
    questions: Record<string, QuestionData>;
};

export const ChecklistDetails: React.FC<ChecklistDetailsProps> = ({
    project,
    disabled,
    noFooter,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projectId, inspectionId, step } = useParams();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const questions = useSelector(
        (state: RootStates) =>
            state.conductInspection.checklistDetails.questions
    );
    const selectedChecklist = useSelector(
        (state: RootStates) =>
            state.conductInspection.projectDetails?.selectedChecklist
    );

    const checklistFilledCount = useSelector(
        (state: RootStates) =>
            Object.values(
                state.conductInspection.checklistDetails.questions ?? {}
            ).filter((q) => q.response).length
    );
    const formik = useFormik({
        initialValues: {
            questions: questions,
        } as InitialValues,
        onSubmit: () => {
            if (checklistFilledCount !== selectedChecklist?.data?.length) {
                toast.error("Please fill in all the checklists");
            } else {
                setShowConfirmation(true);
            }
        },
    });

    useEffect(() => {
        if (
            Object.values(questions ?? {}).length &&
            !Object.values(formik.values.questions ?? {}).length
        ) {
            formik.setFieldValue("questions", questions);
        }
    }, [formik, questions]);

    const handleSubmit = () => {
        const processedData = Object.entries(questions).reduce(
            (
                acc: Record<
                    string,
                    Partial<QuestionData> & { status?: string }
                >,
                [key, val]
            ) => {
                acc[key] = {
                    comments:
                        val.comments?.length > 0
                            ? val.comments.map((c) => ({
                                  ...c,
                                  documents: val.documents ?? [],
                              }))
                            : val.documents?.length > 0
                            ? [
                                  {
                                      comment: "",
                                      documents: val.documents ?? [],
                                  },
                              ]
                            : [],
                    response: val.response?.toUpperCase(),
                    requireReinspection: val.requireReinspection,

                    question:
                        selectedChecklist?.data?.find((q) => q._id === key)
                            ?.question ?? "",
                    status: "DRAFT",
                };

                return acc;
            },
            {}
        );
        delete processedData.questions;

        onSubmit?.({
            projectId: projectId!,
            inspectionId: inspectionId!,
            page: step!,
            body: processedData,
        });
        setShowConfirmation(false);
    };

    const getResolutionChangeHandler = (key: string, value: any) => () => {
        const questions = {
            ...formik.values.questions,
            [key]: {
                ...(formik.values.questions?.[key] ?? {}),
                response: value,
                markFilled: true,
            },
        };
        formik.setFieldValue("questions", questions);
        dispatch(setChecklistDetails(questions));
    };

    const getReInspectionChangeHandler =
        (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
            const questions = {
                ...formik.values.questions,
                [key]: {
                    ...(formik.values.questions?.[key] ?? {}),
                    requireReinspection: e.target.checked,
                },
            };
            formik.setFieldValue("questions", questions);
            dispatch(setChecklistDetails(questions));
        };

    const getCommentChangeHandler =
        (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
            const questions = {
                ...formik.values.questions,
                [key]: {
                    ...(formik.values.questions?.[key] ?? {}),
                    comments: [
                        {
                            ...(formik.values.questions?.[key] ?? {})
                                .comments?.[0],
                            comment: e.target.value,
                        },
                    ],
                },
            };
            formik.setFieldValue("questions", questions);
            dispatch(setChecklistDetails(questions));
        };

    const getCaptureImageHandler = (key: string) => (image: string) => {
        const documents = [
            ...(formik.values.questions[key]?.documents ?? []),
            { docType: "image", url: image },
        ];
        const questions = {
            ...formik.values.questions,
            [key]: {
                ...(formik.values.questions?.[key] ?? {}),
                documents,
            },
        };
        formik.setFieldValue("questions", questions);
        dispatch(setChecklistDetails(questions));
    };

    const getCaptureImageDeleteHandler =
        (key: string) => (documents: string[]) => {
            const questions = {
                ...formik.values.questions,
                [key]: {
                    ...(formik.values.questions?.[key] ?? {}),
                    documents: documents.map((doc) => ({
                        docType: "image",
                        url: doc,
                    })),
                },
            };
            formik.setFieldValue("questions", questions);
            dispatch(setChecklistDetails(questions));
        };

    const handleBack = () =>
        navigate(
            `/project/${project.projectInfo.id}/conduct-inspection/${inspectionId}/1`
        );

    const handleCloseConfirmation = () => setShowConfirmation(false);

    return (
        <Form
            className="checklist-details d-flex flex-column"
            onSubmit={formik.handleSubmit}
        >
            <div className="label-container d-flex flex-column flex-md-row justify-center align-items-start">
                <Form.Label>
                    {!disabled && t("fill_your")}{" "}
                    <span className="checklist-label">{`#${selectedChecklist?.name} `}</span>
                    {t("checklist")}
                </Form.Label>
                {!disabled && (
                    <span
                        className={`que-indicator ${
                            checklistFilledCount ===
                            selectedChecklist?.data?.length
                                ? "completed"
                                : ""
                        }`}
                    >
                        {`${
                            (selectedChecklist?.data?.length ?? 0) -
                            checklistFilledCount
                        } of ${selectedChecklist?.data?.length} ${t(
                            "questions_remaining"
                        )}`}
                    </span>
                )}
            </div>
            <div className="d-flex flex-column form-content">
                <ul className="list-group">
                    {selectedChecklist?.data?.map?.((q, i) => (
                        <li
                            key={q._id}
                            className="list-group-item d-flex flex-column border-none"
                        >
                            <label>
                                <b>{`${t("question")} - ${i + 1}`}</b>
                            </label>
                            <div className="d-flex flex-column border que-container">
                                <span className="px-3 py-2">{q.question}</span>
                                <div className="options d-flex justify-content-between align-items-center">
                                    <div className="input-group border-top px-3 py-2 w-100 que-options">
                                        <Form.Check
                                            checked={
                                                formik.values.questions?.[q._id]
                                                    ?.response === "YES"
                                            }
                                            disabled={disabled}
                                            inline
                                            label={t("label_yes")}
                                            type="radio"
                                            onChange={getResolutionChangeHandler(
                                                q._id,
                                                "YES"
                                            )}
                                        />
                                        <Form.Check
                                            checked={
                                                formik.values.questions?.[q._id]
                                                    ?.response === "NO"
                                            }
                                            inline
                                            disabled={disabled}
                                            label={t("label_no")}
                                            type="radio"
                                            onChange={getResolutionChangeHandler(
                                                q._id,
                                                "NO"
                                            )}
                                        />
                                        <Form.Check
                                            checked={
                                                formik.values.questions?.[q._id]
                                                    ?.response === "NA"
                                            }
                                            inline
                                            disabled={disabled}
                                            label={t("label_na")}
                                            type="radio"
                                            onChange={getResolutionChangeHandler(
                                                q._id,
                                                "NA"
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <Form.Check
                                name="re-inspection"
                                label={t("label_required_re_inspection")}
                                disabled={disabled}
                                checked={
                                    formik.values.questions?.[q._id]
                                        ?.requireReinspection ?? false
                                }
                                onChange={getReInspectionChangeHandler(q._id)}
                            />
                            {disabled ? (
                                formik.values.questions?.[q._id]?.comments?.[0]
                                    ?.inspectionComment && (
                                    <div className="d-flex flex-column flex-md-row justify-content-between additional-controls-container">
                                        <FormInput
                                            name="comments"
                                            label=""
                                            as="textarea"
                                            value={
                                                formik.values.questions?.[q._id]
                                                    ?.comments?.[0]?.comment ??
                                                ""
                                            }
                                            placeholder={t(
                                                "placehold_add_comments"
                                            )}
                                            disabled={disabled}
                                            onChange={getCommentChangeHandler(
                                                q._id
                                            )}
                                        />
                                        <FormCaptureInput
                                            images={
                                                formik.values.questions?.[
                                                    q._id
                                                ]?.documents?.map(
                                                    (doc) => doc.url
                                                ) ?? []
                                            }
                                            limit={3}
                                            path={`projects/${projectId}/conduct-inspection/${inspectionId}/questions/${
                                                q._id
                                            }/${Date.now()}`}
                                            disabled={disabled}
                                            onCapture={getCaptureImageHandler(
                                                q._id
                                            )}
                                            onDelete={getCaptureImageDeleteHandler(
                                                q._id
                                            )}
                                        />
                                    </div>
                                )
                            ) : (
                                <div className="d-flex flex-column flex-md-row justify-content-between additional-controls-container">
                                    <FormInput
                                        name="comments"
                                        label=""
                                        as="textarea"
                                        value={
                                            formik.values.questions?.[q._id]
                                                ?.comments?.[0]?.comment ?? ""
                                        }
                                        placeholder={t(
                                            "placehold_add_comments"
                                        )}
                                        disabled={disabled}
                                        onChange={getCommentChangeHandler(
                                            q._id
                                        )}
                                    />
                                    <FormCaptureInput
                                        images={
                                            formik.values.questions?.[
                                                q._id
                                            ]?.documents?.map(
                                                (doc) => doc.url
                                            ) ?? []
                                        }
                                        limit={3}
                                        path={`projects/${projectId}/conduct-inspection/${inspectionId}/questions/${
                                            q._id
                                        }/${Date.now()}`}
                                        disabled={disabled}
                                        onCapture={getCaptureImageHandler(
                                            q._id
                                        )}
                                        onDelete={getCaptureImageDeleteHandler(
                                            q._id
                                        )}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
            {!noFooter && (
                <div className="d-flex justify-content-between">
                    <Button onClick={handleBack}>{t("btn_back")}</Button>
                    <Button type="submit">{t("btn_next")}</Button>
                </div>
            )}
            <Modal
                show={showConfirmation}
                withIcon
                type="success"
                title=""
                modalClassName="checklist-modal"
                footerContentAlignment="center"
                submitBtnText={t("btn_save")}
                cancelBtnText={t("btn_goback")}
                onSubmit={handleSubmit}
                onClose={handleCloseConfirmation}
            />
        </Form>
    );
};
